import { useState } from 'react'
import { History, ShieldCheck, Truck, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  useAssignProductTier,
  useDeleteProductMapOverride,
  useProductSpecialNetHistory,
  useSetProductManufacturerTerms,
  useSetProductManualNet,
  useSetProductMapOverride,
} from '@/api/hooks'
import type { components } from '@/api/schema'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { ActionPopover } from '@/components/action-popover'
import { InlineFieldGroup, type InlineGroupField } from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { SourceEmailLink } from '@/features/manufacturer-data/source-email-link'
import { formatMoney, formatPercent, parseUTCDate } from '@/lib/utils'

type Product = components['schemas']['DashboardProductDetailRead']

const NO_TIER_VALUE = '__none__'

function extractErrorDetail(err: unknown): string {
  if (err && typeof err === 'object' && 'detail' in err) {
    const detail = (err as { detail: unknown }).detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) =>
          item && typeof item === 'object' && 'msg' in item ? String(item.msg) : null,
        )
        .filter(Boolean)
      if (messages.length) return messages.join(', ')
    }
    if (detail && typeof detail === 'object' && 'violations' in detail) {
      const violations = (detail as { violations: unknown }).violations
      if (Array.isArray(violations)) return violations.map(String).join(', ')
    }
  }
  if (err instanceof Error) return err.message
  return 'Request failed'
}

function asNumber(v: string | number | null | undefined): number | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

function moneyInputValue(value: number | null): string {
  return value == null ? '' : value.toFixed(2)
}

function humanizeToken(value: string | null | undefined) {
  if (!value) return 'Not available'
  const labels: Record<string, string> = {
    aq: 'AutoQuotes',
    discount_chain: 'Manufacturer discount',
    manual: 'Special NET',
    net_item: 'NET item (no discount)',
    pricing_terms: 'Reviewed pricing',
  }
  const normalized = value.trim().toLowerCase()
  if (labels[normalized]) return labels[normalized]
  return value
    .replaceAll('_', ' ')
    .replaceAll('-', ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())
}

/** A saved price edit's toast; a product on Shopify also hears its price is on the way (USA-1671). */
function priceSaved(message: string, onShopify: boolean) {
  toast.success(onShopify ? `${message}. Updating Shopify…` : message)
}

/** A typed money amount the old number boxes accepted: filled and above $0. */
function positiveAmount(value: string) {
  const amount = Number(value)
  return value.trim() !== '' && Number.isFinite(amount) && amount > 0
}

// -----------------------------------------------------------------------------
// MAP override, edited in place (USA-1580)
// -----------------------------------------------------------------------------

type OverrideField = 'price' | 'notes'

const OVERRIDE_FIELDS: ReadonlyArray<InlineGroupField<OverrideField>> = [
  {
    key: 'price',
    label: 'MAP price (USD)',
    required: true,
    inputMode: 'decimal',
    className: 'col-span-6 sm:col-span-2',
  },
  { key: 'notes', label: 'Notes', className: 'col-span-6 sm:col-span-4' },
]

function MapOverrideEditor({
  productUuid,
  currentPrice,
  onShopify,
  children,
}: {
  productUuid: string
  currentPrice: number | null
  onShopify: boolean
  children: React.ReactNode
}) {
  const [editing, setEditing] = useState(false)
  const mutation = useSetProductMapOverride(productUuid)

  function submit(next: Record<OverrideField, string>) {
    mutation.mutate(
      { map_price: next.price.trim(), notes: next.notes.trim() || null },
      {
        onSuccess: () => {
          priceSaved('MAP override saved', onShopify)
          setEditing(false)
        },
        onError: (err) => toast.error(extractErrorDetail(err)),
      },
    )
  }

  return (
    <InlineFieldGroup
      label="MAP override"
      fields={OVERRIDE_FIELDS}
      // Notes start empty each time, as the old popover's did.
      values={{ price: moneyInputValue(currentPrice), notes: '' }}
      invalid={(draft) => (positiveAmount(draft.price) ? [] : ['price'])}
      notice="An AQ MAP higher than this value wins. Enter a MAP price greater than $0."
      pending={mutation.isPending}
      editing={editing}
      onEditingChange={setEditing}
      onSave={submit}
    >
      {children}
    </InlineFieldGroup>
  )
}

// -----------------------------------------------------------------------------
// Special NET, edited in place with its required reason (USA-1580)
// -----------------------------------------------------------------------------

type SpecialNetField = 'price' | 'reason'

const SPECIAL_NET_FIELDS: ReadonlyArray<InlineGroupField<SpecialNetField>> = [
  {
    key: 'price',
    label: 'Special NET (USD)',
    required: true,
    inputMode: 'decimal',
    className: 'col-span-6 sm:col-span-2',
  },
  { key: 'reason', label: 'Reason', required: true, kind: 'textarea' },
]

function SpecialNetEditor({
  productUuid,
  currentNet,
  currentReason,
  onShopify,
  children,
}: {
  productUuid: string
  currentNet: number | null
  currentReason: string | null
  onShopify: boolean
  children: React.ReactNode
}) {
  const [editing, setEditing] = useState(false)
  const mutation = useSetProductManualNet(productUuid)

  function submit(next: Record<SpecialNetField, string>) {
    mutation.mutate(
      { manual_net_price: next.price.trim(), reason: next.reason.trim() || null },
      {
        onSuccess: () => {
          priceSaved('Special NET saved', onShopify)
          setEditing(false)
        },
        onError: (err) => toast.error(extractErrorDetail(err)),
      },
    )
  }

  return (
    <InlineFieldGroup
      label="Special NET"
      fields={SPECIAL_NET_FIELDS}
      values={{ price: moneyInputValue(currentNet), reason: currentReason ?? '' }}
      invalid={(draft) => (positiveAmount(draft.price) ? [] : ['price'])}
      notice="Highest precedence — won't be overwritten by AQ sync. Enter a Special NET greater than $0 and a reason for this audited change."
      pending={mutation.isPending}
      editing={editing}
      onEditingChange={setEditing}
      onSave={submit}
    >
      {children}
    </InlineFieldGroup>
  )
}

// -----------------------------------------------------------------------------
// Manufacturer cost terms: the tariff edits in place, free freight is a switch (USA-1580)
// -----------------------------------------------------------------------------

type TariffField = 'rate' | 'label'

function TariffTermsEditor({ product, children }: { product: Product; children: React.ReactNode }) {
  const currentTariffLabel = product.manufacturer.tariff_label ?? ''
  const effectiveTariffLabel = currentTariffLabel.trim() || 'Tariff'
  const [editing, setEditing] = useState(false)
  const mutation = useSetProductManufacturerTerms(product.uuid)
  const fields: ReadonlyArray<InlineGroupField<TariffField>> = [
    {
      key: 'rate',
      label: `${effectiveTariffLabel} on NET`,
      inputMode: 'decimal',
      suffix: '%',
    },
    { key: 'label', label: 'PO surcharge line' },
  ]

  function submit(next: Record<TariffField, string>) {
    const numericTariff = next.rate.trim() === '' ? null : Number(next.rate)
    const normalizedTariffLabel = next.label.trim()
    const body: components['schemas']['ProductManufacturerTermsUpdate'] = {}
    if (numericTariff !== asNumber(product.manufacturer.tariff_percentage))
      body.tariff_percentage = next.rate.trim() || null
    if (normalizedTariffLabel !== currentTariffLabel.trim())
      body.tariff_label = normalizedTariffLabel || null
    // The old dialog kept Save disabled until a term really changed.
    if (Object.keys(body).length === 0) {
      setEditing(false)
      return
    }
    mutation.mutate(body, {
      onSuccess: (result) => {
        toast.success(
          result.repriced_products
            ? `Manufacturer terms saved; ${result.repriced_products} products repriced`
            : 'Manufacturer terms saved',
        )
        setEditing(false)
      },
      onError: (err) => toast.error(extractErrorDetail(err)),
    })
  }

  return (
    <InlineFieldGroup
      label="manufacturer terms"
      fields={fields}
      values={{
        rate:
          product.manufacturer.tariff_percentage == null
            ? ''
            : String(product.manufacturer.tariff_percentage),
        label: currentTariffLabel,
      }}
      invalid={(draft) => {
        const rate = draft.rate.trim() === '' ? null : Number(draft.rate)
        const rateValid = rate == null || (Number.isFinite(rate) && rate >= 0 && rate <= 100)
        return [
          ...(rateValid ? [] : (['rate'] as const)),
          ...(draft.label.trim().length > 100 ? (['label'] as const) : []),
        ]
      }}
      notice={
        <>
          Changes apply to every {product.manufacturer.name} product. At 5%, a $3,099.38 NET costs
          $3,254.35 before margin. Enter a rate between 0% and 100%, or leave it blank.
        </>
      }
      saveLabel="Save terms"
      pending={mutation.isPending}
      editing={editing}
      onEditingChange={setEditing}
      onSave={submit}
    >
      {children}
    </InlineFieldGroup>
  )
}

function FreeFreightSwitch({ product }: { product: Product }) {
  const mutation = useSetProductManufacturerTerms(product.uuid)
  const id = `manufacturer-free-freight-${product.uuid}`

  function change(checked: boolean) {
    mutation.mutate(
      { manufacturer_free_freight: checked },
      {
        onSuccess: (result) =>
          toast.success(
            result.repriced_products
              ? `Manufacturer terms saved; ${result.repriced_products} products repriced`
              : 'Manufacturer terms saved',
          ),
        onError: (err) => toast.error(extractErrorDetail(err)),
      },
    )
  }

  return (
    <div className="flex items-center gap-2">
      <Switch
        id={id}
        checked={product.manufacturer_free_freight === true}
        disabled={mutation.isPending}
        aria-describedby={`${id}-hint`}
        onCheckedChange={change}
      />
      <Label htmlFor={id} className="text-xs font-normal">
        Manufacturer-paid freight
      </Label>
      <span id={`${id}-hint`} className="sr-only">
        Marks {product.model} as free freight from the manufacturer. It does not make customer
        shipping free.
      </span>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Main card
// -----------------------------------------------------------------------------

export function ProductMapNetCard({ product }: { product: Product }) {
  const policy = product.manufacturer.map_policy
  const isTieredPolicy = policy?.policy_type === 'tiered'

  const assignTier = useAssignProductTier(product.uuid)
  const deleteOverride = useDeleteProductMapOverride(product.uuid)
  const clearManualNet = useSetProductManualNet(product.uuid)
  const history = useProductSpecialNetHistory(product.uuid)
  const [showAllHistory, setShowAllHistory] = useState(false)

  const overridePrice = asNumber(product.map_override?.map_price as string | undefined)
  const manualNet = asNumber(product.manual_net_price as string | undefined)
  const currentNetSource = product.current_price?.net_source ?? null
  const currentMapSource = product.current_price?.map_source ?? null
  const discount = product.manufacturer_discount
  const listPrice = asNumber(product.current_price?.list_price as string | undefined)
  const calcNet = asNumber(product.current_price?.calculated_net_price as string | undefined)
  const baseNet = asNumber(product.current_price?.base_net_price as string | undefined)
  const effectiveCost = asNumber(product.current_price?.net_price as string | undefined)
  const tariffRate = asNumber(
    product.current_price?.manufacturer_tariff_percentage as string | undefined,
  )
  const tariffAmount = asNumber(product.current_price?.tariff_amount as string | undefined)
  const tariffLabel = product.manufacturer.tariff_label?.trim() || 'Tariff'
  const onShopify = !!product.shopify_product_id

  function changeTier(value: string) {
    const next = value === NO_TIER_VALUE ? null : value
    assignTier.mutate(next, {
      onSuccess: () => priceSaved(next ? 'Tier assigned' : 'Tier cleared', onShopify),
      onError: (err) => toast.error(extractErrorDetail(err)),
    })
  }

  function clearOverride() {
    deleteOverride.mutate(undefined, {
      onSuccess: () => priceSaved('Override cleared', onShopify),
      onError: (err) => toast.error(extractErrorDetail(err)),
    })
  }

  function clearManual() {
    clearManualNet.mutate(
      { manual_net_price: null },
      {
        onSuccess: () => priceSaved('Special NET cleared', onShopify),
        onError: (err) => toast.error(extractErrorDetail(err)),
      },
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" />
          Product pricing controls
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-background p-3">
            <p className="text-xs text-muted-foreground">Base NET</p>
            <p className="mt-1 font-medium tabular-nums">{formatMoney(baseNet)}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Source: {humanizeToken(currentNetSource)}
            </p>
          </div>
          <div className="bg-background p-3">
            <TariffTermsEditor product={product}>
              <span className="block text-xs text-muted-foreground">{tariffLabel}</span>
              <span className="mt-1 block font-medium tabular-nums">
                {tariffRate == null
                  ? 'Not applied'
                  : `${formatPercent(tariffRate)} · ${formatMoney(tariffAmount)}`}
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">Manufacturer term</span>
            </TariffTermsEditor>
          </div>
          <div className="bg-background p-3">
            <p className="text-xs text-muted-foreground">Effective cost</p>
            <p className="mt-1 text-lg font-semibold tabular-nums">{formatMoney(effectiveCost)}</p>
            <p className="mt-1 text-xs text-muted-foreground">Base NET + {tariffLabel}</p>
          </div>
          <div className="bg-background p-3">
            <p className="text-xs text-muted-foreground">Freight from manufacturer</p>
            <p className="mt-1 flex items-center gap-1.5 font-medium">
              <Truck className="h-4 w-4" />
              {product.manufacturer_free_freight
                ? 'Free for this product'
                : 'Standard freight terms'}
            </p>
            <div className="mt-2">
              <FreeFreightSwitch product={product} />
            </div>
          </div>
        </div>
        {/* Policy summary */}
        {policy ? (
          <div className="rounded-md bg-muted/40 p-3 text-sm">
            <span className="font-medium">{policy.policy_label}</span>{' '}
            <Badge
              variant={policy.status === 'active' ? 'default' : 'secondary'}
              className="ml-1 px-1.5 py-0 text-[10px]"
            >
              {humanizeToken(policy.status)}
            </Badge>
            <span className="ml-2 text-muted-foreground">
              {policy.policy_type === 'flat_percent' || policy.policy_type === 'hybrid'
                ? `chain ${policy.default_discount_chain ?? '—'}`
                : humanizeToken(policy.policy_type)}
            </span>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Manufacturer has no MAP policy configured.
          </p>
        )}

        {/* Tier assignment (tiered policies only) */}
        {isTieredPolicy && policy?.tiers && policy.tiers.length > 0 && (
          <div className="space-y-1">
            <Label>Tier</Label>
            <Select
              value={product.map_tier?.uuid ?? NO_TIER_VALUE}
              onValueChange={changeTier}
              disabled={assignTier.isPending}
            >
              <SelectTrigger>
                <SelectValue placeholder="No tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TIER_VALUE}>No explicit tier (use default)</SelectItem>
                {policy.tiers.map((t) => (
                  <SelectItem key={t.id} value={t.uuid}>
                    {t.name} — {t.discount_chain}
                    {t.is_default ? ' (default)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* MAP override row */}
        <div className="flex items-start justify-between gap-2 border-t pt-3">
          <div className="min-w-0 flex-1">
            <MapOverrideEditor
              productUuid={product.uuid}
              currentPrice={overridePrice}
              onShopify={onShopify}
            >
              <span className="block space-y-0.5">
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">
                  Product MAP override
                </span>
                <span className="block text-sm">
                  {overridePrice != null ? (
                    <span className="font-medium">{formatMoney(overridePrice)}</span>
                  ) : (
                    <span className="text-muted-foreground">— not set —</span>
                  )}
                  {currentMapSource && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      Effective MAP source: {humanizeToken(currentMapSource)}
                    </span>
                  )}
                </span>
              </span>
            </MapOverrideEditor>
          </div>
          <div className="flex gap-1">
            {overridePrice != null && (
              <ActionPopover
                align="end"
                trigger={
                  <LoadingButton
                    variant="ghost"
                    size="icon"
                    loading={deleteOverride.isPending}
                    aria-label="Clear MAP override"
                  >
                    <Trash2 className="h-4 w-4" />
                  </LoadingButton>
                }
                title="Clear this product MAP override?"
                confirmLabel="Clear"
                confirmVariant="destructive"
                onConfirm={(_note, close) => {
                  close()
                  clearOverride()
                }}
              />
            )}
          </div>
        </div>

        {/* Manufacturer discount chain row */}
        <div className="flex flex-wrap items-start justify-between gap-2 border-t pt-3">
          <div className="space-y-0.5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Manufacturer discount chain
            </p>
            {discount ? (
              <>
                <p className="text-sm">
                  {discount.discount ? (
                    <span className="font-mono font-medium">{discount.discount}</span>
                  ) : (
                    <span className="text-muted-foreground italic">no chain set</span>
                  )}
                  {currentNetSource === 'discount_chain' && (
                    <Badge variant="default" className="ml-2 px-1.5 py-0 text-[10px]">
                      drives NET
                    </Badge>
                  )}
                  {currentNetSource === 'net_item' && (
                    <Badge variant="outline" className="ml-2 px-1.5 py-0 text-[10px]">
                      not applied
                    </Badge>
                  )}
                </p>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  {discount.name && (
                    <p>
                      Category: <span className="text-foreground">{discount.name}</span>
                    </p>
                  )}
                  {discount.buying_group && <p>Buying group: {discount.buying_group}</p>}
                  {currentNetSource === 'net_item' && (
                    <p>
                      AutoQuotes marks this item NET, so the discount does not apply. NET is the
                      AutoQuotes NET
                      {calcNet != null && (
                        <>
                          {' '}
                          <span className="text-foreground font-medium">{formatMoney(calcNet)}</span>
                        </>
                      )}
                      .
                    </p>
                  )}
                  {currentNetSource === 'discount_chain' &&
                    discount.discount &&
                    listPrice != null &&
                    calcNet != null && (
                      <p>
                        Applied: {formatMoney(listPrice)} list →{' '}
                        <span className="text-foreground font-medium">
                          {formatMoney(calcNet)} net
                        </span>{' '}
                        ({discount.discount})
                      </p>
                    )}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">— no manufacturer discount linked —</p>
            )}
          </div>
        </div>

        {/* Special NET row */}
        <div className="flex items-start justify-between gap-2 border-t pt-3">
          <div className="min-w-0 flex-1">
            <SpecialNetEditor
              productUuid={product.uuid}
              currentNet={manualNet}
              currentReason={product.manual_net_price_reason ?? null}
              onShopify={onShopify}
            >
              <span className="block space-y-0.5">
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">
                  Special NET
                </span>
                <span className="block text-sm">
                  {manualNet != null ? (
                    <span className="font-medium">{formatMoney(manualNet)}</span>
                  ) : (
                    <span className="text-muted-foreground">— not set —</span>
                  )}
                  {currentNetSource && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      Effective NET source: {humanizeToken(currentNetSource)}
                    </span>
                  )}
                </span>
                {product.manual_net_price_reason && (
                  <span className="block text-xs text-muted-foreground">
                    Reason: {product.manual_net_price_reason}
                  </span>
                )}
              </span>
            </SpecialNetEditor>
          </div>
          <div className="flex gap-1">
            {manualNet != null && (
              <ActionPopover
                align="end"
                trigger={
                  <LoadingButton
                    variant="ghost"
                    size="icon"
                    loading={clearManualNet.isPending}
                    aria-label="Clear Special NET"
                  >
                    <Trash2 className="h-4 w-4" />
                  </LoadingButton>
                }
                title="Clear Special NET?"
                confirmLabel="Clear"
                confirmVariant="destructive"
                onConfirm={(_note, close) => {
                  close()
                  clearManual()
                }}
              >
                <p className="text-xs text-muted-foreground">
                  AQ / discount-chain NET will take over.
                </p>
              </ActionPopover>
            )}
          </div>
        </div>
        <div className="border-t pt-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium">
            <History className="h-4 w-4" /> Special NET history
          </div>
          {history.isLoading ? (
            <p role="status" className="text-xs text-muted-foreground">
              Loading history…
            </p>
          ) : history.isError ? (
            <div className="flex items-center justify-between gap-3 text-xs text-destructive">
              <span>Could not load Special NET history.</span>
              <Button variant="outline" size="sm" onClick={() => void history.refetch()}>
                Try again
              </Button>
            </div>
          ) : history.data?.length ? (
            <div className="space-y-2">
              {(showAllHistory ? history.data : history.data.slice(0, 5)).map((event) => (
                <div key={event.uuid} className="min-w-0 rounded-md bg-muted/40 p-2 text-xs">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="min-w-0 break-words">
                      <span className="font-medium capitalize">{event.event_type}</span>{' '}
                      {event.previous_net_price == null
                        ? 'Not set'
                        : formatMoney(event.previous_net_price)}
                      {' → '}
                      {event.net_price == null ? 'Cleared' : formatMoney(event.net_price)}
                    </span>
                    <span className="text-muted-foreground">
                      {parseUTCDate(event.created_at).toLocaleString()}
                    </span>
                  </div>
                  {event.reason && (
                    <p className="mt-1 break-words text-muted-foreground">{event.reason}</p>
                  )}
                  {event.source_email && (
                    <p className="mt-1">
                      <SourceEmailLink source={event.source_email} />
                    </p>
                  )}
                </div>
              ))}
              {history.data.length > 5 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAllHistory((value) => !value)}
                >
                  {showAllHistory ? 'Show recent' : `Show all ${history.data.length}`}
                </Button>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No Special NET changes recorded.</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
